// User detail page — subscription summary + invoices list. From here the
// admin can pay any UNPAID invoice (which also runs the subscription reset).
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box, Typography, Card, CardContent, Button, Chip, Stack, Divider,
    Table, TableHead, TableBody, TableRow, TableCell, TableContainer, Paper,
    Pagination, CircularProgress, Alert, Switch, Snackbar, IconButton, Tooltip,
    Dialog, DialogTitle, DialogContent, DialogActions, MenuItem, TextField,
    ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PaymentIcon from '@mui/icons-material/Payment';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import LoopIcon from '@mui/icons-material/Loop';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import KeyOutlinedIcon from '@mui/icons-material/KeyOutlined';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import StorageIcon from '@mui/icons-material/Storage';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import NoteAddOutlinedIcon from '@mui/icons-material/NoteAddOutlined';

import axiosInstance from '../utils/axiosInstance';
import { formatCurrency, formatDate, formatDateTime, titleCase } from '../utils/format';
import PayInvoiceDialog from '../components/PayInvoiceDialog';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const PAGE_SIZE = 25;

const statusChipColor = {
    paid: 'success', unpaid: 'warning', void: 'default',
};

const subStatusColor = {
    active: 'success', trialing: 'info', freemium: 'default',
    past_due: 'error', canceled: 'default', incomplete: 'warning',
    requires_payment_method: 'warning',
};

const UserDetail = () => {
    const { userId } = useParams();
    const navigate = useNavigate();

    const [detail, setDetail] = useState(null);
    const [invoices, setInvoices] = useState([]);
    const [invPage, setInvPage] = useState(1);
    const [invPages, setInvPages] = useState(1);
    const [invTotal, setInvTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [invLoading, setInvLoading] = useState(false);
    const [error, setError] = useState(null);
    const [snackbar, setSnackbar] = useState(null);
    const [toggling, setToggling] = useState(false);
    const [payInvoice, setPayInvoice] = useState(null);
    // Phase C admin actions on the subscription. Each opens its own dialog so
    // the user has to explicitly confirm; nothing fires from a single click.
    const [changePlanOpen, setChangePlanOpen] = useState(false);
    const [switchCycleOpen, setSwitchCycleOpen] = useState(false);
    const [cancelOpen, setCancelOpen] = useState(false);
    const [actionBusy, setActionBusy] = useState(false);

    // Phase E: per-user deep-dive data (chatbots, KB, payments, spending).
    // Loaded lazily on mount alongside the existing detail/invoices fetches.
    const [chatbots, setChatbots] = useState([]);
    const [chatbotsTotal, setChatbotsTotal] = useState(0);
    const [chatbotsPage, setChatbotsPage] = useState(1);
    const [kbSummary, setKbSummary] = useState(null);
    const [payments, setPayments] = useState([]);
    const [paymentsTotal, setPaymentsTotal] = useState(0);
    const [paymentsPage, setPaymentsPage] = useState(1);
    const [spending, setSpending] = useState(null);
    const [passwordOpen, setPasswordOpen] = useState(false);
    // Phase G state: subscription override + invoice CRUD dialogs.
    const [editSubOpen, setEditSubOpen] = useState(false);
    const [deleteInvoice, setDeleteInvoice] = useState(null);
    const [sendInvoice, setSendInvoice] = useState(null);
    const [generateInvoiceOpen, setGenerateInvoiceOpen] = useState(false);

    const loadDetail = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const r = await axiosInstance.get(`/admin/users/${userId}`);
            setDetail(r.data);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load user.');
        } finally {
            setLoading(false);
        }
    }, [userId]);

    const loadInvoices = useCallback(async () => {
        setInvLoading(true);
        try {
            const r = await axiosInstance.get(`/admin/users/${userId}/invoices`, {
                params: { page: invPage, limit: PAGE_SIZE },
            });
            setInvoices(r.data?.invoices || []);
            setInvPages(r.data?.pages || 1);
            setInvTotal(r.data?.total || 0);
        } catch (err) {
            setSnackbar({ severity: 'error', message: 'Could not load invoices.' });
        } finally {
            setInvLoading(false);
        }
    }, [userId, invPage]);

    // Phase E loaders. Each is small + paginated; the dashboard intentionally
    // shows a few rows per card with its own "Load more" pagination control.
    const loadChatbots = useCallback(async () => {
        try {
            const r = await axiosInstance.get(`/admin/users/${userId}/chatbots`, {
                params: { page: chatbotsPage, limit: 5 },
            });
            setChatbots(r.data?.chatbots || []);
            setChatbotsTotal(r.data?.total || 0);
        } catch (err) { /* surface in a snackbar only if the user noticed; KB/chatbots are non-critical. */ }
    }, [userId, chatbotsPage]);

    const loadKbSummary = useCallback(async () => {
        try {
            const r = await axiosInstance.get(`/admin/users/${userId}/kb-summary`);
            setKbSummary(r.data || null);
        } catch (err) { setKbSummary(null); }
    }, [userId]);

    const loadPayments = useCallback(async () => {
        try {
            const r = await axiosInstance.get(`/admin/users/${userId}/payments`, {
                params: { page: paymentsPage, limit: 5 },
            });
            setPayments(r.data?.payments || []);
            setPaymentsTotal(r.data?.total || 0);
        } catch (err) { /* non-critical */ }
    }, [userId, paymentsPage]);

    const loadSpending = useCallback(async () => {
        try {
            const r = await axiosInstance.get(`/admin/users/${userId}/spending`);
            setSpending(r.data || null);
        } catch (err) { setSpending(null); }
    }, [userId]);

    useEffect(() => { loadDetail(); }, [loadDetail]);
    useEffect(() => { loadInvoices(); }, [loadInvoices]);
    useEffect(() => { loadChatbots(); }, [loadChatbots]);
    useEffect(() => { loadKbSummary(); }, [loadKbSummary]);
    useEffect(() => { loadPayments(); }, [loadPayments]);
    useEffect(() => { loadSpending(); }, [loadSpending]);

    const toggleActive = async () => {
        if (!detail?.user) return;
        setToggling(true);
        try {
            const r = await axiosInstance.patch(`/admin/users/${detail.user.user_id}/active`, {
                is_active: !detail.user.is_active,
            });
            setDetail((d) => ({ ...d, user: { ...d.user, is_active: !d.user.is_active } }));
            setSnackbar({ severity: 'success', message: r.data?.message || 'Updated.' });
        } catch (err) {
            setSnackbar({
                severity: 'error',
                message: err.response?.data?.message || 'Could not update user.',
            });
        } finally {
            setToggling(false);
        }
    };

    const onPaid = (data) => {
        setSnackbar({ severity: 'success', message: 'Invoice settled. Subscription reset.' });
        setPayInvoice(null);
        // Optimistic patch: if the returned data has the updated sub, refresh.
        loadDetail();
        loadInvoices();
    };

    // --- Phase C subscription control handlers ---
    // Each posts to a NON-charging admin endpoint; user keeps their current
    // period (no surprise charge), the new amount kicks in at natural renewal.
    const handleChangePlan = async (planId, interval) => {
        if (!subscription) return;
        setActionBusy(true);
        try {
            const body = { plan_id: planId };
            if (interval) body.interval = interval;
            const res = await axiosInstance.patch(
                `/admin/users/${detail.user.user_id}/subscription/plan`, body,
            );
            setSnackbar({ severity: 'success', message: res.data?.message || 'Plan changed.' });
            setChangePlanOpen(false);
            loadDetail();
        } catch (err) {
            setSnackbar({
                severity: 'error',
                message: err.response?.data?.message || 'Could not change plan.',
            });
        } finally {
            setActionBusy(false);
        }
    };

    const handleSwitchCycle = async () => {
        if (!subscription) return;
        setActionBusy(true);
        const target = subscription.interval === 'year' ? 'month' : 'year';
        try {
            const res = await axiosInstance.patch(
                `/admin/users/${detail.user.user_id}/subscription/interval`,
                { interval: target },
            );
            setSnackbar({ severity: 'success', message: res.data?.message || 'Cycle switched.' });
            setSwitchCycleOpen(false);
            loadDetail();
        } catch (err) {
            setSnackbar({
                severity: 'error',
                message: err.response?.data?.message || 'Could not switch cycle.',
            });
        } finally {
            setActionBusy(false);
        }
    };

    const handleCancelSubscription = async () => {
        if (!subscription) return;
        setActionBusy(true);
        try {
            const res = await axiosInstance.post(
                `/admin/users/${detail.user.user_id}/subscription/cancel`,
            );
            setSnackbar({ severity: 'success', message: res.data?.message || 'Subscription cancelled.' });
            setCancelOpen(false);
            loadDetail();
        } catch (err) {
            setSnackbar({
                severity: 'error',
                message: err.response?.data?.message || 'Could not cancel subscription.',
            });
        } finally {
            setActionBusy(false);
        }
    };

    const handleUpdatePassword = async (newPassword) => {
        if (!detail?.user) return;
        setActionBusy(true);
        try {
            await axiosInstance.post(`/admin/users/${detail.user.user_id}/password`, {
                new_password: newPassword,
            });
            setSnackbar({ severity: 'success', message: 'Password updated. The user must use the new password to log in.' });
            setPasswordOpen(false);
        } catch (err) {
            setSnackbar({
                severity: 'error',
                message: err.response?.data?.message || 'Could not update password.',
            });
        } finally {
            setActionBusy(false);
        }
    };

    // --- Phase G handlers -------------------------------------------------
    const handleEditSubscription = async (payload) => {
        if (!subscription) return;
        setActionBusy(true);
        try {
            const res = await axiosInstance.patch(
                `/admin/users/${detail.user.user_id}/subscription`, payload,
            );
            setSnackbar({ severity: 'success', message: res.data?.message || 'Subscription updated.' });
            setEditSubOpen(false);
            loadDetail();
        } catch (err) {
            setSnackbar({
                severity: 'error',
                message: err.response?.data?.message || 'Could not update subscription.',
            });
        } finally {
            setActionBusy(false);
        }
    };

    const handleDeleteInvoice = async () => {
        if (!deleteInvoice) return;
        setActionBusy(true);
        try {
            await axiosInstance.delete(`/admin/invoices/${deleteInvoice.id}`);
            setSnackbar({ severity: 'success', message: 'Invoice deleted.' });
            setDeleteInvoice(null);
            loadInvoices();
        } catch (err) {
            setSnackbar({
                severity: 'error',
                message: err.response?.data?.message || 'Could not delete invoice.',
            });
        } finally {
            setActionBusy(false);
        }
    };

    const handleSendInvoice = async (recipientEmail) => {
        if (!sendInvoice) return;
        setActionBusy(true);
        try {
            const body = {};
            // Only send a recipient override when the admin actually changed it,
            // so the backend's "use billed_to_email by default" behaviour still
            // works on a plain confirm.
            const trimmed = (recipientEmail || '').trim();
            if (trimmed && trimmed.toLowerCase() !== (sendInvoice.billed_to_email || '').toLowerCase()) {
                body.recipient_email = trimmed;
            }
            const res = await axiosInstance.post(`/admin/invoices/${sendInvoice.id}/send`, body);
            setSnackbar({ severity: 'success', message: res.data?.message || 'Invoice sent.' });
            setSendInvoice(null);
            loadInvoices();
        } catch (err) {
            setSnackbar({
                severity: 'error',
                message: err.response?.data?.message || 'Could not send invoice.',
            });
        } finally {
            setActionBusy(false);
        }
    };

    const handleGenerateInvoice = async (payload) => {
        if (!detail?.user) return;
        setActionBusy(true);
        try {
            const res = await axiosInstance.post(
                `/admin/users/${detail.user.user_id}/invoices/generate`, payload,
            );
            setSnackbar({ severity: 'success', message: res.data?.message || 'Invoice generated.' });
            setGenerateInvoiceOpen(false);
            loadInvoices();
        } catch (err) {
            setSnackbar({
                severity: 'error',
                message: err.response?.data?.message || 'Could not generate invoice.',
            });
        } finally {
            setActionBusy(false);
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                <CircularProgress />
            </Box>
        );
    }
    if (error) {
        return (
            <>
                <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/users')}
                        sx={{ fontFamily, textTransform: 'none', mb: 2 }}>
                    Back to users
                </Button>
                <Alert severity="error" sx={{ fontFamily }}>{error}</Alert>
            </>
        );
    }
    if (!detail) return null;

    const { user, subscription } = detail;

    return (
        <Box sx={{ fontFamily }}>
            <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/users')}
                    sx={{ fontFamily, textTransform: 'none', mb: 2 }}>
                Back to users
            </Button>

            {/* User card */}
            <Card variant="outlined" sx={{ borderRadius: '14px', mb: 3 }}>
                <CardContent>
                    <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between"
                           alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={1.5}>
                        <Box>
                            <Typography sx={{ fontFamily, fontSize: '1.25rem', fontWeight: 800, color: '#0f172a' }}>
                                {user.user_email}
                            </Typography>
                            <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                                <Chip size="small" label={`#${user.user_id}`} sx={{ fontFamily }} />
                                <Chip size="small" label={user.account_type || 'individual'} sx={{ fontFamily }} />
                                {user.verified
                                    ? <Chip size="small" color="success" label="Verified" sx={{ fontFamily }} />
                                    : <Chip size="small" color="warning" label="Unverified" sx={{ fontFamily }} />}
                                {user.is_active
                                    ? <Chip size="small" color="success" label="Active" sx={{ fontFamily }} />
                                    : <Chip size="small" color="error" label="Deactivated" sx={{ fontFamily }} />}
                            </Stack>
                        </Box>
                        <Stack direction="row" spacing={1.5} alignItems="center">
                            <Button
                                size="small" variant="outlined"
                                startIcon={<KeyOutlinedIcon fontSize="small" />}
                                onClick={() => setPasswordOpen(true)}
                                sx={{ fontFamily, textTransform: 'none' }}
                            >
                                Update password
                            </Button>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#475569' }}>
                                    {user.is_active ? 'Deactivate' : 'Re-activate'}
                                </Typography>
                                <Switch checked={!!user.is_active} onChange={toggleActive} disabled={toggling} />
                            </Stack>
                        </Stack>
                    </Stack>
                </CardContent>
            </Card>

            {/* Subscription card */}
            <Card variant="outlined" sx={{ borderRadius: '14px', mb: 3 }}>
                <CardContent>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                        <Typography sx={{ fontFamily, fontWeight: 700, color: '#0f172a' }}>
                            Current subscription
                        </Typography>
                        {subscription && (
                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                <Button
                                    size="small" variant="outlined"
                                    startIcon={<EditOutlinedIcon fontSize="small" />}
                                    onClick={() => setEditSubOpen(true)}
                                    sx={{ fontFamily, textTransform: 'none' }}
                                >
                                    Edit
                                </Button>
                                <Button
                                    size="small" variant="outlined"
                                    startIcon={<SwapHorizIcon fontSize="small" />}
                                    onClick={() => setChangePlanOpen(true)}
                                    sx={{ fontFamily, textTransform: 'none' }}
                                >
                                    Change plan
                                </Button>
                                {Number(subscription.plan?.price) > 0 && (
                                    <Button
                                        size="small" variant="outlined"
                                        startIcon={<LoopIcon fontSize="small" />}
                                        onClick={() => setSwitchCycleOpen(true)}
                                        sx={{ fontFamily, textTransform: 'none' }}
                                    >
                                        Switch to {subscription.interval === 'year' ? 'monthly' : 'yearly'}
                                    </Button>
                                )}
                                {subscription.status !== 'canceled' && subscription.status !== 'freemium' && (
                                    <Button
                                        size="small" variant="outlined" color="error"
                                        startIcon={<CancelOutlinedIcon fontSize="small" />}
                                        onClick={() => setCancelOpen(true)}
                                        sx={{ fontFamily, textTransform: 'none' }}
                                    >
                                        Cancel
                                    </Button>
                                )}
                            </Stack>
                        )}
                    </Stack>
                    {subscription ? (
                        <>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}
                                   justifyContent="space-between" alignItems={{ sm: 'center' }}>
                                <Box>
                                    <Typography sx={{ fontFamily, fontSize: '1.05rem', fontWeight: 700 }}>
                                        {subscription.plan?.display_name || '--'}
                                    </Typography>
                                    {/* Subtitle: show ONLY what the customer is actually billed.
                                        Yearly subs show the yearly total + "Save 10%" badge so the
                                        admin doesn't have to mentally multiply 250 × 12 × 0.9. */}
                                    {Number(subscription.plan?.price) > 0 ? (
                                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.25 }}>
                                            <Typography sx={{ fontFamily, fontSize: '0.82rem', color: '#64748b' }}>
                                                {subscription.interval === 'year'
                                                    ? `Billed ${formatCurrency(subscription.effective_price ?? (subscription.plan.price * 12 * 0.9), subscription.plan.currency)} / year`
                                                    : `Billed ${formatCurrency(subscription.effective_price ?? subscription.plan.price, subscription.plan.currency)} / month`}
                                            </Typography>
                                            {subscription.interval === 'year' && Number(subscription.discount_value) > 0 && (
                                                <Chip size="small" color="success"
                                                      label={`Save ${Number(subscription.discount_value)}%`}
                                                      sx={{ fontFamily, height: 18, fontSize: '0.65rem', fontWeight: 700 }} />
                                            )}
                                        </Stack>
                                    ) : (
                                        <Typography sx={{ fontFamily, fontSize: '0.82rem', color: '#64748b' }}>
                                            Free plan
                                        </Typography>
                                    )}
                                </Box>
                                <Chip
                                    size="small"
                                    color={subStatusColor[subscription.status] || 'default'}
                                    label={titleCase(subscription.status)}
                                    sx={{ fontFamily, fontWeight: 700 }}
                                />
                            </Stack>
                            <Divider sx={{ my: 1.5 }} />
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3}>
                                <DetailItem label="Billing cycle"
                                            value={subscription.interval === 'year'
                                                ? 'Yearly' : 'Monthly'} />
                                <DetailItem label="Discount"
                                            value={Number(subscription.discount_value) > 0
                                                ? `${Number(subscription.discount_value)}${subscription.discount_type === 'percentage' ? '%' : ''} off`
                                                : '—'} />
                                <DetailItem label="Effective price"
                                            value={Number(subscription.plan?.price) > 0
                                                ? `${formatCurrency(subscription.effective_price ?? subscription.plan?.price, subscription.plan?.currency)} / ${subscription.interval || 'month'}`
                                                : 'Free'} />
                            </Stack>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} sx={{ mt: 2 }}>
                                <DetailItem label="Period start" value={formatDate(subscription.current_period_start)} />
                                <DetailItem label="Period end" value={formatDate(subscription.current_period_end)} />
                                <DetailItem label="Grace ends" value={formatDate(subscription.grace_period_ends_at)} />
                                <DetailItem label="Trial ends" value={formatDate(subscription.trial_ends_at)} />
                            </Stack>
                            {subscription.last_charge_error && (
                                <Alert severity="warning" sx={{ fontFamily, mt: 2 }}>
                                    Last charge error: {subscription.last_charge_error}
                                </Alert>
                            )}
                        </>
                    ) : (
                        <Typography sx={{ fontFamily, color: '#64748b' }}>
                            No subscription on file.
                        </Typography>
                    )}
                </CardContent>
            </Card>

            {/* Invoices */}
            <Card variant="outlined" sx={{ borderRadius: '14px' }}>
                <CardContent>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                        <Typography sx={{ fontFamily, fontWeight: 700, color: '#0f172a' }}>
                            Invoices
                        </Typography>
                        <Stack direction="row" spacing={1.5} alignItems="center">
                            <Typography sx={{ fontFamily, fontSize: '0.8rem', color: '#94a3b8' }}>
                                {invTotal} total
                            </Typography>
                            <Button
                                size="small" variant="outlined"
                                startIcon={<NoteAddOutlinedIcon fontSize="small" />}
                                onClick={() => setGenerateInvoiceOpen(true)}
                                sx={{ fontFamily, textTransform: 'none' }}
                            >
                                Generate invoice
                            </Button>
                        </Stack>
                    </Stack>
                    {invLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                            <CircularProgress size={28} />
                        </Box>
                    ) : invoices.length === 0 ? (
                        <Typography sx={{ fontFamily, color: '#64748b', py: 3, textAlign: 'center' }}>
                            No invoices yet.
                        </Typography>
                    ) : (
                        <>
                            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: '10px' }}>
                                <Table size="small">
                                    <TableHead sx={{ backgroundColor: '#f8fafc' }}>
                                        <TableRow>
                                            <TableCell sx={{ fontFamily, fontWeight: 700 }}>Invoice</TableCell>
                                            <TableCell sx={{ fontFamily, fontWeight: 700 }}>Issued</TableCell>
                                            <TableCell sx={{ fontFamily, fontWeight: 700 }}>Period</TableCell>
                                            <TableCell sx={{ fontFamily, fontWeight: 700 }} align="right">Amount</TableCell>
                                            <TableCell sx={{ fontFamily, fontWeight: 700 }}>Status</TableCell>
                                            <TableCell sx={{ fontFamily, fontWeight: 700 }}>Paid via</TableCell>
                                            <TableCell sx={{ fontFamily, fontWeight: 700 }} align="right">Actions</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {invoices.map((inv) => (
                                            <TableRow key={inv.id} hover>
                                                <TableCell sx={{ fontFamily, fontWeight: 600 }}>
                                                    {inv.invoice_number}
                                                </TableCell>
                                                <TableCell sx={{ fontFamily }}>
                                                    {formatDate(inv.issued_at)}
                                                </TableCell>
                                                <TableCell sx={{ fontFamily, color: '#64748b' }}>
                                                    {inv.period_label || '--'}
                                                </TableCell>
                                                <TableCell sx={{ fontFamily }} align="right">
                                                    {formatCurrency(inv.total, inv.currency)}
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        size="small"
                                                        color={statusChipColor[inv.status] || 'default'}
                                                        label={titleCase(inv.status)}
                                                        sx={{ fontFamily, fontWeight: 600 }}
                                                    />
                                                </TableCell>
                                                <TableCell sx={{ fontFamily, color: '#64748b' }}>
                                                    {inv.paid_via
                                                        ? `${titleCase(inv.paid_via)}${inv.check_number ? ` #${inv.check_number}` : ''}`
                                                        : '--'}
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center">
                                                        {inv.status === 'unpaid' && (
                                                            <Button
                                                                size="small" variant="contained"
                                                                startIcon={<PaymentIcon fontSize="small" />}
                                                                onClick={() => setPayInvoice(inv)}
                                                                sx={{ fontFamily, textTransform: 'none', fontWeight: 700 }}
                                                            >
                                                                Pay
                                                            </Button>
                                                        )}
                                                        {inv.status !== 'unpaid' && (
                                                            <Tooltip title={`Paid ${formatDateTime(inv.paid_at)}`}>
                                                                <span style={{ color: '#94a3b8', fontFamily, fontSize: '0.78rem', marginRight: 8 }}>
                                                                    {inv.paid_at ? formatDate(inv.paid_at) : '--'}
                                                                </span>
                                                            </Tooltip>
                                                        )}
                                                        <Tooltip title="Re-send to customer">
                                                            <span>
                                                                <IconButton size="small"
                                                                            disabled={!inv.billed_to_email}
                                                                            onClick={() => setSendInvoice(inv)}>
                                                                    <SendOutlinedIcon fontSize="small" />
                                                                </IconButton>
                                                            </span>
                                                        </Tooltip>
                                                        <Tooltip title="Delete invoice">
                                                            <IconButton size="small" color="error"
                                                                        onClick={() => setDeleteInvoice(inv)}>
                                                                <DeleteOutlineIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Stack>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            {invPages > 1 && (
                                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                                    <Pagination count={invPages} page={invPage}
                                                onChange={(_, p) => setInvPage(p)}
                                                size="small" color="primary" />
                                </Box>
                            )}
                        </>
                    )}
                </CardContent>
            </Card>

            {/* Phase E section cards. Order: spending (the headline number) ->
                payments (detail of where the money came from) -> chatbots
                (what the user built) -> KB summary (data they uploaded). */}
            <SpendingCard spending={spending} />
            <PaymentsCard
                payments={payments} total={paymentsTotal}
                page={paymentsPage} onPage={setPaymentsPage}
            />
            <ChatbotsCard
                chatbots={chatbots} total={chatbotsTotal}
                page={chatbotsPage} onPage={setChatbotsPage}
            />
            <KnowledgeBaseCard kbSummary={kbSummary} />

            <PayInvoiceDialog
                open={!!payInvoice} invoice={payInvoice}
                onClose={() => setPayInvoice(null)}
                onPaid={onPaid}
            />

            <UpdatePasswordDialog
                open={passwordOpen} busy={actionBusy}
                userEmail={user?.user_email}
                onClose={() => !actionBusy && setPasswordOpen(false)}
                onConfirm={handleUpdatePassword}
            />

            <EditSubscriptionDialog
                open={editSubOpen} busy={actionBusy} subscription={subscription}
                onClose={() => !actionBusy && setEditSubOpen(false)}
                onConfirm={handleEditSubscription}
            />

            <DeleteInvoiceDialog
                open={!!deleteInvoice} busy={actionBusy} invoice={deleteInvoice}
                onClose={() => !actionBusy && setDeleteInvoice(null)}
                onConfirm={handleDeleteInvoice}
            />

            <SendInvoiceDialog
                open={!!sendInvoice} busy={actionBusy} invoice={sendInvoice}
                onClose={() => !actionBusy && setSendInvoice(null)}
                onConfirm={handleSendInvoice}
            />

            <GenerateInvoiceDialog
                open={generateInvoiceOpen} busy={actionBusy}
                subscription={subscription}
                onClose={() => !actionBusy && setGenerateInvoiceOpen(false)}
                onConfirm={handleGenerateInvoice}
            />

            <ChangePlanDialog
                open={changePlanOpen} busy={actionBusy} subscription={subscription}
                onClose={() => !actionBusy && setChangePlanOpen(false)}
                onConfirm={handleChangePlan}
            />

            <SwitchCycleDialog
                open={switchCycleOpen} busy={actionBusy} subscription={subscription}
                onClose={() => !actionBusy && setSwitchCycleOpen(false)}
                onConfirm={handleSwitchCycle}
            />

            <ConfirmCancelDialog
                open={cancelOpen} busy={actionBusy} subscription={subscription}
                onClose={() => !actionBusy && setCancelOpen(false)}
                onConfirm={handleCancelSubscription}
            />

            <Snackbar
                open={!!snackbar} autoHideDuration={4500} onClose={() => setSnackbar(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                {snackbar ? (
                    <Alert severity={snackbar.severity} onClose={() => setSnackbar(null)} sx={{ fontFamily }}>
                        {snackbar.message}
                    </Alert>
                ) : null}
            </Snackbar>
        </Box>
    );
};

const DetailItem = ({ label, value }) => (
    <Box>
        <Typography sx={{ fontFamily, fontSize: '0.72rem', fontWeight: 700, letterSpacing: 0.5,
                          textTransform: 'uppercase', color: '#94a3b8' }}>
            {label}
        </Typography>
        <Typography sx={{ fontFamily, fontSize: '0.92rem', color: '#1e293b' }}>
            {value || '--'}
        </Typography>
    </Box>
);

// --- Change Plan dialog ------------------------------------------------------
// Lets the admin force-move a user to a different plan AND/OR a different
// billing cycle in one shot. No charge is fired -- the user finishes their
// current period and is billed the new plan/cycle at the natural renewal.
const ChangePlanDialog = ({ open, busy, subscription, onClose, onConfirm }) => {
    const [plans, setPlans] = useState([]);
    const [loadingPlans, setLoadingPlans] = useState(false);
    const [selectedPlanId, setSelectedPlanId] = useState('');
    const [interval, setInterval] = useState('month');

    useEffect(() => {
        if (!open) return;
        setSelectedPlanId(subscription?.plan_id || '');
        setInterval(subscription?.interval || 'month');
        setLoadingPlans(true);
        axiosInstance.get('/admin/plans', { params: { limit: 200 } })
            .then((res) => setPlans(res.data?.plans || []))
            .catch(() => setPlans([]))
            .finally(() => setLoadingPlans(false));
    }, [open, subscription?.plan_id, subscription?.interval]);

    const targetPlan = plans.find((p) => p.id === Number(selectedPlanId));
    const isPaidTarget = targetPlan && Number(targetPlan.price) > 0;
    const sameAsCurrent = (
        Number(selectedPlanId) === subscription?.plan_id
        && (!isPaidTarget || interval === (subscription?.interval || 'month'))
    );

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
            <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>Change plan</DialogTitle>
            <DialogContent dividers>
                <Stack spacing={2}>
                    <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#475569' }}>
                        Force-change this subscription. The user keeps access for the rest of their
                        current period; the new plan/cycle takes effect at the next natural renewal
                        (no charge is fired now).
                    </Typography>
                    {loadingPlans ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                            <CircularProgress size={22} />
                        </Box>
                    ) : (
                        <TextField
                            select fullWidth size="small" label="Target plan"
                            value={selectedPlanId}
                            onChange={(e) => setSelectedPlanId(e.target.value)}
                        >
                            <MenuItem value="" disabled>Select a plan…</MenuItem>
                            {plans.map((p) => (
                                <MenuItem key={p.id} value={p.id}>
                                    {p.display_name} ({p.name})
                                    {p.price > 0 ? ` — ${formatCurrency(p.price, p.currency)}/${p.interval || 'month'}` : ' — Free'}
                                    {p.account_type ? ` · ${p.account_type}` : ''}
                                </MenuItem>
                            ))}
                        </TextField>
                    )}
                    {isPaidTarget && (
                        <Box>
                            <Typography sx={{ fontFamily, fontSize: '0.78rem', color: '#475569', mb: 0.75 }}>
                                Billing cycle
                            </Typography>
                            <ToggleButtonGroup
                                value={interval} exclusive size="small" color="primary"
                                onChange={(_, v) => v && setInterval(v)}
                            >
                                <ToggleButton value="month" sx={{ fontFamily, textTransform: 'none', px: 3 }}>
                                    Monthly
                                </ToggleButton>
                                <ToggleButton value="year" sx={{ fontFamily, textTransform: 'none', px: 3 }}>
                                    Yearly · save 10%
                                </ToggleButton>
                            </ToggleButtonGroup>
                            <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#94a3b8', mt: 0.75 }}>
                                {interval === 'year'
                                    ? `Next renewal will be charged ${formatCurrency((Number(targetPlan.price) || 0) * 12 * 0.9, targetPlan.currency)} for the year.`
                                    : `Next renewal will be charged ${formatCurrency(Number(targetPlan.price) || 0, targetPlan.currency)} for the month.`}
                            </Typography>
                        </Box>
                    )}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={busy}
                        sx={{ fontFamily, textTransform: 'none' }}>
                    Cancel
                </Button>
                <Button
                    onClick={() => onConfirm(Number(selectedPlanId), isPaidTarget ? interval : null)}
                    variant="contained" disabled={busy || !selectedPlanId || sameAsCurrent}
                    sx={{ fontFamily, textTransform: 'none', fontWeight: 700 }}
                >
                    {busy ? <CircularProgress size={18} /> : 'Apply change'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

// --- Switch cycle dialog -----------------------------------------------------
// One-button confirmation for the most common operation: flip an active
// paid subscription between monthly and yearly without any charge.
const SwitchCycleDialog = ({ open, busy, subscription, onClose, onConfirm }) => {
    if (!subscription) return null;
    const target = subscription.interval === 'year' ? 'month' : 'year';
    const monthly = Number(subscription.plan?.price) || 0;
    const newAmount = target === 'year'
        ? monthly * 12 * 0.9
        : monthly;
    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
            <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>
                Switch to {target}ly billing
            </DialogTitle>
            <DialogContent dividers>
                <Stack spacing={1.5}>
                    <Typography sx={{ fontFamily, fontSize: '0.9rem', color: '#1e293b' }}>
                        Change this subscription from <strong>{subscription.interval || 'month'}ly</strong> to{' '}
                        <strong>{target}ly</strong> billing?
                    </Typography>
                    <Alert severity="info" sx={{ fontFamily }}>
                        The user keeps access for the rest of their current period. No charge fires now.
                        At the next natural renewal ({formatDate(subscription.current_period_end) || 'soon'}),
                        the scheduler will charge <strong>{formatCurrency(newAmount, subscription.plan?.currency)}</strong>{' '}
                        for the next {target}.
                    </Alert>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={busy}
                        sx={{ fontFamily, textTransform: 'none' }}>
                    Cancel
                </Button>
                <Button
                    onClick={onConfirm} variant="contained" disabled={busy}
                    sx={{ fontFamily, textTransform: 'none', fontWeight: 700 }}
                >
                    {busy ? <CircularProgress size={18} /> : `Switch to ${target}ly`}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

// --- Confirm cancel dialog ---------------------------------------------------
// Cancel-at-period-end: user keeps access until current_period_end, after
// which the existing process_scheduled_cancellations_job downgrades them.
const ConfirmCancelDialog = ({ open, busy, subscription, onClose, onConfirm }) => {
    if (!subscription) return null;
    const endsAt = subscription.current_period_end;
    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
            <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>Cancel subscription</DialogTitle>
            <DialogContent dividers>
                <Stack spacing={1.5}>
                    <Typography sx={{ fontFamily, fontSize: '0.9rem' }}>
                        Cancel <strong>{subscription.plan?.display_name}</strong> for this user?
                    </Typography>
                    <Alert severity="warning" sx={{ fontFamily }}>
                        The user keeps access until{' '}
                        <strong>{formatDate(endsAt) || 'the end of their current period'}</strong>.
                        After that they're moved to Freemium automatically.
                    </Alert>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={busy}
                        sx={{ fontFamily, textTransform: 'none' }}>
                    Keep subscription
                </Button>
                <Button
                    onClick={onConfirm} color="error" variant="contained" disabled={busy}
                    sx={{ fontFamily, textTransform: 'none', fontWeight: 700 }}
                >
                    {busy ? <CircularProgress size={18} /> : 'Cancel subscription'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

// --- Phase E section cards --------------------------------------------------
// Tiny presentational components -- the parent page owns all data + paging
// state. Each accepts only what it renders.

const SpendingCard = ({ spending }) => {
    if (!spending) return null;
    const usd = spending.by_currency?.usd;
    const total = usd?.total || 0;
    const count = usd?.count || 0;
    const monthly = spending.monthly_trailing_6 || [];
    const maxMonth = Math.max(0.01, ...monthly.map((m) => m.amount || 0));
    return (
        <Card variant="outlined" sx={{ borderRadius: '14px', mb: 3 }}>
            <CardContent>
                <Stack direction="row" alignItems="flex-end" spacing={2} sx={{ mb: 2 }}>
                    <Box>
                        <Typography sx={{ fontFamily, fontWeight: 700, color: '#0f172a' }}>
                            Total spent
                        </Typography>
                        <Typography sx={{ fontFamily, fontSize: '0.78rem', color: '#94a3b8' }}>
                            Sum of all successful charges, all-time.
                        </Typography>
                    </Box>
                    <Box sx={{ flex: 1 }} />
                    <Box sx={{ textAlign: 'right' }}>
                        <Typography sx={{ fontFamily, fontSize: '1.6rem', fontWeight: 800, color: '#10b981' }}>
                            {formatCurrency(total, 'usd')}
                        </Typography>
                        <Typography sx={{ fontFamily, fontSize: '0.72rem', color: '#94a3b8' }}>
                            {count} successful charge{count !== 1 ? 's' : ''}
                        </Typography>
                    </Box>
                </Stack>
                {monthly.length > 0 && (
                    <>
                        <Divider sx={{ mb: 1.5 }} />
                        <Typography sx={{ fontFamily, fontSize: '0.7rem', fontWeight: 700,
                                          letterSpacing: 0.5, textTransform: 'uppercase',
                                          color: '#475569', mb: 1 }}>
                            Trailing 6 months
                        </Typography>
                        <Stack direction="row" spacing={1.5} alignItems="flex-end" sx={{ height: 80 }}>
                            {monthly.map((m) => {
                                const pct = Math.round(((m.amount || 0) / maxMonth) * 100);
                                return (
                                    <Box key={m.month} sx={{ flex: 1, textAlign: 'center' }}>
                                        <Tooltip title={`${m.month}: ${formatCurrency(m.amount || 0, 'usd')}`}>
                                            <Box sx={{
                                                height: `${Math.max(pct, 2)}%`, minHeight: 2,
                                                bgcolor: '#10b981', opacity: m.amount > 0 ? 1 : 0.25,
                                                borderRadius: '4px 4px 0 0', mb: 0.5,
                                            }} />
                                        </Tooltip>
                                        <Typography sx={{ fontFamily, fontSize: '0.65rem', color: '#94a3b8' }}>
                                            {m.month.slice(5)}
                                        </Typography>
                                    </Box>
                                );
                            })}
                        </Stack>
                    </>
                )}
            </CardContent>
        </Card>
    );
};

const PaymentsCard = ({ payments, total, page, onPage }) => {
    const PAGE_SIZE = 5;
    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
    const statusColor = {
        succeeded: 'success', failed: 'error', pending: 'warning', requires_action: 'warning',
    };
    return (
        <Card variant="outlined" sx={{ borderRadius: '14px', mb: 3 }}>
            <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                    <Typography sx={{ fontFamily, fontWeight: 700, color: '#0f172a' }}>
                        Payments
                    </Typography>
                    <Typography sx={{ fontFamily, fontSize: '0.8rem', color: '#94a3b8' }}>
                        {total} total
                    </Typography>
                </Stack>
                {payments.length === 0 ? (
                    <Typography sx={{ fontFamily, color: '#64748b', py: 3, textAlign: 'center' }}>
                        No payments yet.
                    </Typography>
                ) : (
                    <>
                        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: '10px' }}>
                            <Table size="small">
                                <TableHead sx={{ backgroundColor: '#f8fafc' }}>
                                    <TableRow>
                                        <TableCell sx={{ fontFamily, fontWeight: 700 }}>Date</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 700 }}>Description</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 700 }} align="right">Amount</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 700 }}>Status</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 700 }}>Gateway ID</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {payments.map((p) => (
                                        <TableRow key={p.id} hover>
                                            <TableCell sx={{ fontFamily, color: '#475569' }}>
                                                {formatDate(p.processed_at || p.created_at)}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, color: '#1e293b' }}>
                                                {p.description || '--'}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, fontWeight: 700 }} align="right">
                                                {formatCurrency(p.amount, p.currency)}
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    size="small"
                                                    color={statusColor[p.status] || 'default'}
                                                    label={titleCase(p.status)}
                                                    sx={{ fontFamily, fontWeight: 600 }}
                                                />
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, color: '#94a3b8', fontSize: '0.75rem' }}>
                                                {p.gateway_transaction_id ? (
                                                    <Tooltip title={p.gateway_transaction_id}>
                                                        <span>{p.gateway_transaction_id.slice(0, 14)}…</span>
                                                    </Tooltip>
                                                ) : '--'}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        {totalPages > 1 && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1.5 }}>
                                <Pagination
                                    count={totalPages} page={page}
                                    onChange={(_, p) => onPage(p)}
                                    size="small" color="primary"
                                />
                            </Box>
                        )}
                    </>
                )}
            </CardContent>
        </Card>
    );
};

const ChatbotsCard = ({ chatbots, total, page, onPage }) => {
    const PAGE_SIZE = 5;
    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
    return (
        <Card variant="outlined" sx={{ borderRadius: '14px', mb: 3 }}>
            <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <SmartToyIcon fontSize="small" sx={{ color: '#0ea5e9' }} />
                        <Typography sx={{ fontFamily, fontWeight: 700, color: '#0f172a' }}>
                            Chatbots
                        </Typography>
                    </Stack>
                    <Typography sx={{ fontFamily, fontSize: '0.8rem', color: '#94a3b8' }}>
                        {total} total
                    </Typography>
                </Stack>
                {chatbots.length === 0 ? (
                    <Typography sx={{ fontFamily, color: '#64748b', py: 3, textAlign: 'center' }}>
                        No chatbots yet.
                    </Typography>
                ) : (
                    <>
                        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: '10px' }}>
                            <Table size="small">
                                <TableHead sx={{ backgroundColor: '#f8fafc' }}>
                                    <TableRow>
                                        <TableCell sx={{ fontFamily, fontWeight: 700 }}>ID</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 700 }}>Name</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 700 }}>Type</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 700 }}>Status</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 700 }}>Created</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {chatbots.map((c) => (
                                        <TableRow key={c.id} hover>
                                            <TableCell sx={{ fontFamily, fontWeight: 600 }}>#{c.id}</TableCell>
                                            <TableCell sx={{ fontFamily }}>
                                                {c.name}
                                                {c.is_default && (
                                                    <Chip size="small" label="Default"
                                                          sx={{ fontFamily, ml: 1, height: 18, fontSize: '0.65rem' }} />
                                                )}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, color: '#64748b' }}>
                                                {c.type || '--'}
                                            </TableCell>
                                            <TableCell>
                                                {c.is_process_completed
                                                    ? <Chip size="small" color="success" label="Ready"
                                                            sx={{ fontFamily, fontWeight: 600 }} />
                                                    : <Chip size="small" color="warning" label="Setup"
                                                            sx={{ fontFamily, fontWeight: 600 }} />}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, color: '#94a3b8' }}>
                                                {formatDate(c.created_at)}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        {totalPages > 1 && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1.5 }}>
                                <Pagination
                                    count={totalPages} page={page}
                                    onChange={(_, p) => onPage(p)}
                                    size="small" color="primary"
                                />
                            </Box>
                        )}
                    </>
                )}
            </CardContent>
        </Card>
    );
};

const KnowledgeBaseCard = ({ kbSummary }) => {
    if (!kbSummary) return null;
    const items = [
        { label: 'Documents', value: kbSummary.documents },
        { label: 'Websites', value: kbSummary.websites },
        { label: 'Images', value: kbSummary.images },
        { label: 'Chatbots', value: kbSummary.chatbots },
    ];
    return (
        <Card variant="outlined" sx={{ borderRadius: '14px', mb: 3 }}>
            <CardContent>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                    <StorageIcon fontSize="small" sx={{ color: '#7c3aed' }} />
                    <Typography sx={{ fontFamily, fontWeight: 700, color: '#0f172a' }}>
                        Knowledge base
                    </Typography>
                </Stack>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    {items.map((it) => (
                        <Box key={it.label} sx={{
                            flex: 1, p: 1.5, borderRadius: '10px',
                            border: '1px solid #e2e8f0', bgcolor: 'rgba(248,250,252,0.6)',
                            textAlign: 'center',
                        }}>
                            <Typography sx={{ fontFamily, fontSize: '1.4rem', fontWeight: 800, color: '#0f172a' }}>
                                {it.value}
                            </Typography>
                            <Typography sx={{ fontFamily, fontSize: '0.72rem', fontWeight: 700,
                                              textTransform: 'uppercase', color: '#94a3b8',
                                              letterSpacing: 0.5 }}>
                                {it.label}
                            </Typography>
                        </Box>
                    ))}
                </Stack>
            </CardContent>
        </Card>
    );
};

// --- Update password dialog -------------------------------------------------
// The admin types the new password directly (per the agreed default).
// Validation runs both sides: backend enforces >= 6 chars; the dialog blocks
// submission if the confirmation field doesn't match.
const UpdatePasswordDialog = ({ open, busy, userEmail, onClose, onConfirm }) => {
    const [password, setPassword] = useState('');
    const [confirm, setConfirm] = useState('');
    const [show, setShow] = useState(false);

    useEffect(() => {
        if (!open) { setPassword(''); setConfirm(''); setShow(false); }
    }, [open]);

    const mismatch = confirm.length > 0 && password !== confirm;
    const tooShort = password.length > 0 && password.length < 6;
    const canSubmit = password.length >= 6 && password === confirm;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
            <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>Update password</DialogTitle>
            <DialogContent dividers>
                <Stack spacing={2}>
                    <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#475569' }}>
                        Set a new password for <strong>{userEmail}</strong>. The user must
                        use this password to sign in from now on — share it with them
                        through a secure channel.
                    </Typography>
                    <TextField
                        autoFocus fullWidth size="small"
                        type={show ? 'text' : 'password'}
                        label="New password" value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        error={tooShort}
                        helperText={tooShort ? 'At least 6 characters required.' : ' '}
                        InputProps={{
                            endAdornment: (
                                <IconButton size="small" tabIndex={-1}
                                            onClick={() => setShow((v) => !v)}>
                                    {show
                                        ? <VisibilityOffOutlinedIcon fontSize="small" />
                                        : <VisibilityOutlinedIcon fontSize="small" />}
                                </IconButton>
                            ),
                        }}
                    />
                    <TextField
                        fullWidth size="small"
                        type={show ? 'text' : 'password'}
                        label="Confirm new password" value={confirm}
                        onChange={(e) => setConfirm(e.target.value)}
                        error={mismatch}
                        helperText={mismatch ? "Doesn't match the password above." : ' '}
                    />
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={busy}
                        sx={{ fontFamily, textTransform: 'none' }}>
                    Cancel
                </Button>
                <Button
                    onClick={() => onConfirm(password)} variant="contained"
                    disabled={busy || !canSubmit}
                    sx={{ fontFamily, textTransform: 'none', fontWeight: 700 }}
                >
                    {busy ? <CircularProgress size={18} /> : 'Set password'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

// --- Phase G dialogs -------------------------------------------------------

// Edit subscription: lets the admin override status + period dates. Used to
// grant a trial, extend an active period, or unstick an INCOMPLETE row.
// NON-charging -- next renewal naturally bills the new amount.
const SUB_STATUS_OPTIONS = [
    { value: 'active',                  label: 'Active' },
    { value: 'trialing',                label: 'Trialing' },
    { value: 'freemium',                label: 'Freemium' },
    { value: 'past_due',                label: 'Past due' },
    { value: 'canceled',                label: 'Canceled' },
    { value: 'incomplete',              label: 'Incomplete' },
    { value: 'requires_payment_method', label: 'Requires payment method' },
];

// Reformat ISO -> 'YYYY-MM-DDTHH:mm' so <input type="datetime-local"> can
// pre-populate. Returns '' when the field is null.
const isoToLocalInput = (iso) => {
    if (!iso) return '';
    try {
        const d = new Date(iso);
        if (isNaN(d.getTime())) return '';
        const pad = (n) => String(n).padStart(2, '0');
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    } catch { return ''; }
};

const EditSubscriptionDialog = ({ open, busy, subscription, onClose, onConfirm }) => {
    const [status, setStatus] = useState('');
    const [periodStart, setPeriodStart] = useState('');
    const [periodEnd, setPeriodEnd] = useState('');
    const [trialEnds, setTrialEnds] = useState('');
    const [graceEnds, setGraceEnds] = useState('');

    useEffect(() => {
        if (!open || !subscription) return;
        setStatus(subscription.status || '');
        setPeriodStart(isoToLocalInput(subscription.current_period_start));
        setPeriodEnd(isoToLocalInput(subscription.current_period_end));
        setTrialEnds(isoToLocalInput(subscription.trial_ends_at));
        setGraceEnds(isoToLocalInput(subscription.grace_period_ends_at));
    }, [open, subscription]);

    const submit = () => {
        // Send '' as null so the backend can clear the column. Empty status
        // (would be weird) is treated as "no change" by the backend's
        // `if 'status' in data` guard, but we don't send it at all to be safe.
        const payload = {};
        if (status && status !== subscription?.status) payload.status = status;
        payload.current_period_start = periodStart || null;
        payload.current_period_end   = periodEnd   || null;
        payload.trial_ends_at        = trialEnds   || null;
        payload.grace_period_ends_at = graceEnds   || null;
        onConfirm(payload);
    };

    if (!subscription) return null;
    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>Edit subscription</DialogTitle>
            <DialogContent dividers>
                <Stack spacing={2}>
                    <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#475569' }}>
                        Override the subscription state. Common uses: grant a trial
                        (set status = trialing + a future <em>trial ends</em> date), or
                        extend an active period (push <em>period end</em> forward).
                        No charge is fired.
                    </Typography>
                    <TextField
                        select fullWidth size="small"
                        label="Status" value={status}
                        onChange={(e) => setStatus(e.target.value)}
                    >
                        {SUB_STATUS_OPTIONS.map((s) => (
                            <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
                        ))}
                    </TextField>
                    <Stack direction="row" spacing={1.5}>
                        <TextField
                            fullWidth size="small" type="datetime-local"
                            label="Period start" InputLabelProps={{ shrink: true }}
                            value={periodStart}
                            onChange={(e) => setPeriodStart(e.target.value)}
                        />
                        <TextField
                            fullWidth size="small" type="datetime-local"
                            label="Period end" InputLabelProps={{ shrink: true }}
                            value={periodEnd}
                            onChange={(e) => setPeriodEnd(e.target.value)}
                        />
                    </Stack>
                    <Stack direction="row" spacing={1.5}>
                        <TextField
                            fullWidth size="small" type="datetime-local"
                            label="Trial ends" InputLabelProps={{ shrink: true }}
                            value={trialEnds}
                            onChange={(e) => setTrialEnds(e.target.value)}
                        />
                        <TextField
                            fullWidth size="small" type="datetime-local"
                            label="Grace ends" InputLabelProps={{ shrink: true }}
                            value={graceEnds}
                            onChange={(e) => setGraceEnds(e.target.value)}
                        />
                    </Stack>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={busy}
                        sx={{ fontFamily, textTransform: 'none' }}>
                    Cancel
                </Button>
                <Button onClick={submit} variant="contained" disabled={busy}
                        sx={{ fontFamily, textTransform: 'none', fontWeight: 700 }}>
                    {busy ? <CircularProgress size={18} /> : 'Save changes'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

// Delete invoice dialog: confirmation that explicitly names the month/period
// being deleted so the admin can't make a wrong-row mistake.
const DeleteInvoiceDialog = ({ open, busy, invoice, onClose, onConfirm }) => {
    if (!invoice) return null;
    const periodLabel = (() => {
        if (invoice.period_start && invoice.period_end) {
            const s = new Date(invoice.period_start);
            return s.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
        }
        return invoice.billing_reason
            ? titleCase(String(invoice.billing_reason).replace(/_/g, ' '))
            : 'Untagged period';
    })();
    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
            <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>Delete invoice</DialogTitle>
            <DialogContent dividers>
                <Stack spacing={1.5}>
                    <Typography sx={{ fontFamily, fontSize: '0.9rem' }}>
                        Permanently delete invoice <strong>{invoice.invoice_number}</strong>{' '}
                        for <strong>{periodLabel}</strong>?
                    </Typography>
                    <Alert severity="error" sx={{ fontFamily }}>
                        This cannot be undone. The invoice row is removed from the
                        database. Any linked payment transaction stays intact but
                        loses its invoice link.
                    </Alert>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={busy}
                        sx={{ fontFamily, textTransform: 'none' }}>
                    Keep invoice
                </Button>
                <Button onClick={onConfirm} color="error" variant="contained" disabled={busy}
                        sx={{ fontFamily, textTransform: 'none', fontWeight: 700 }}>
                    {busy ? <CircularProgress size={18} /> : 'Delete invoice'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

// Send invoice dialog: defaults to the snapshot billed_to_email but lets the
// admin override it with any address (e.g., resending to a client's accountant
// or to themselves for review). Empty field => disabled Send.
const EMAIL_RX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const SendInvoiceDialog = ({ open, busy, invoice, onClose, onConfirm }) => {
    const [email, setEmail] = useState('');
    useEffect(() => {
        if (open) setEmail(invoice?.billed_to_email || '');
    }, [open, invoice]);

    if (!invoice) return null;
    const trimmed = email.trim();
    const isValid = EMAIL_RX.test(trimmed);
    const isOverride = trimmed && trimmed.toLowerCase() !== (invoice.billed_to_email || '').toLowerCase();

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
            <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>Send invoice</DialogTitle>
            <DialogContent dividers>
                <Stack spacing={1.5}>
                    <Typography sx={{ fontFamily, fontSize: '0.9rem' }}>
                        Email invoice <strong>{invoice.invoice_number}</strong>.
                        Defaults to the billed-to address on file -- edit below to
                        send to a different recipient.
                    </Typography>
                    <TextField
                        autoFocus fullWidth size="small" type="email"
                        label="Recipient email" value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        error={!!trimmed && !isValid}
                        helperText={
                            trimmed && !isValid
                                ? "Doesn't look like a valid email."
                                : (isOverride
                                    ? `Original billed-to: ${invoice.billed_to_email || '—'}`
                                    : ' ')
                        }
                    />
                    {invoice.sent_at && (
                        <Alert severity="info" sx={{ fontFamily }}>
                            Last sent on {formatDateTime(invoice.sent_at)} to {invoice.sent_to_email || invoice.billed_to_email}.
                        </Alert>
                    )}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={busy}
                        sx={{ fontFamily, textTransform: 'none' }}>
                    Cancel
                </Button>
                <Button onClick={() => onConfirm(trimmed)}
                        variant="contained" disabled={busy || !isValid}
                        sx={{ fontFamily, textTransform: 'none', fontWeight: 700 }}>
                    {busy ? <CircularProgress size={18} /> : 'Send now'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

// Generate invoice dialog: manually issue an invoice for the user. Period
// defaults to the subscription's ACTUAL current period (so a yearly subscriber
// gets a one-year default, a monthly subscriber gets one month). Falls back
// to the current calendar month when the subscription has no period yet.
const todayUtcMonthBoundsLocal = (interval) => {
    const now = new Date();
    const first = new Date(now.getFullYear(), now.getMonth(), 1);
    const next = interval === 'year'
        ? new Date(now.getFullYear() + 1, now.getMonth(), 1)
        : new Date(now.getFullYear(), now.getMonth() + 1, 1);
    const pad = (n) => String(n).padStart(2, '0');
    const fmt = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T00:00`;
    return { periodStart: fmt(first), periodEnd: fmt(next) };
};

// Given a local-input datetime string ("YYYY-MM-DDTHH:mm") plus the
// subscription's cycle, return the matching end-of-period (same time, +1 year
// or +1 month). Returns '' if the input is unparseable.
const computePeriodEnd = (localStart, interval) => {
    if (!localStart) return '';
    const d = new Date(localStart);
    if (isNaN(d.getTime())) return '';
    const end = new Date(d);
    if (interval === 'year') end.setFullYear(end.getFullYear() + 1);
    else                     end.setMonth(end.getMonth() + 1);
    const pad = (n) => String(n).padStart(2, '0');
    return `${end.getFullYear()}-${pad(end.getMonth() + 1)}-${pad(end.getDate())}T${pad(end.getHours())}:${pad(end.getMinutes())}`;
};

const GenerateInvoiceDialog = ({ open, busy, subscription, onClose, onConfirm }) => {
    const [periodStart, setPeriodStart] = useState('');
    const [periodEnd, setPeriodEnd] = useState('');
    const [markPaid, setMarkPaid] = useState(false);
    const [send, setSend] = useState(false);
    // While true, changing `periodStart` recomputes `periodEnd` automatically
    // to (start + cycle). Set false the first time the admin manually edits
    // `periodEnd`, so we don't clobber their explicit override.
    const [autoEnd, setAutoEnd] = useState(true);

    const interval = subscription?.interval || 'month';
    const monthlyPrice = Number(subscription?.plan?.price) || 0;
    const isFreemium = monthlyPrice <= 0;

    useEffect(() => {
        if (!open) return;
        // Anchor period_start to the subscription's real start (or today if
        // none). Then ALWAYS compute period_end from start + cycle -- never
        // trust the stored current_period_end blindly: a subscription created
        // before today's cycle-aware fixes can carry a 1-month period_end on
        // a yearly cycle, which would surface the wrong year in this dialog.
        const realStart = subscription?.current_period_start;
        const startLocal = realStart
            ? isoToLocalInput(realStart)
            : todayUtcMonthBoundsLocal(interval).periodStart;
        setPeriodStart(startLocal);
        setPeriodEnd(computePeriodEnd(startLocal, interval));
        setAutoEnd(true);                // start in auto mode each open
        setMarkPaid(isFreemium);          // freemium = always $0 PAID
        setSend(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, subscription]);

    // Compute a preview of the invoice totals using subscription discount
    // (e.g. 10% for yearly subs). Pure presentation -- backend re-derives.
    const subtotal = interval === 'year' ? monthlyPrice * 12 : monthlyPrice;
    const discountPct = (subscription?.discount_type === 'percentage')
        ? Number(subscription?.discount_value) || 0
        : 0;
    const discountFixed = (subscription?.discount_type === 'fixed')
        ? Number(subscription?.discount_value) || 0
        : 0;
    const discountAmount = discountPct > 0
        ? Math.round(subtotal * discountPct) / 100
        : Math.min(discountFixed, subtotal);
    const total = Math.max(0, Math.round((subtotal - discountAmount) * 100) / 100);
    const cycleLabel = interval === 'year' ? 'year' : 'month';

    const handleStartChange = (val) => {
        setPeriodStart(val);
        // Auto-snap end-date to (start + cycle) until the admin overrides it.
        if (autoEnd) setPeriodEnd(computePeriodEnd(val, interval));
    };

    const submit = () => {
        onConfirm({
            period_start: periodStart || null,
            period_end: periodEnd || null,
            mark_paid: markPaid,
            send,
        });
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
            <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>Generate invoice</DialogTitle>
            <DialogContent dividers>
                <Stack spacing={2}>
                    <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#475569' }}>
                        Create a new invoice for this <strong>{cycleLabel}ly</strong> subscription.
                        Editing the start date auto-fills the end date one {cycleLabel}{' '}
                        later. The scheduler will skip the same period to avoid duplicates.
                    </Typography>

                    <Stack direction="row" spacing={1.5}>
                        <TextField
                            fullWidth size="small" type="datetime-local"
                            label={`Period start (1 ${cycleLabel})`}
                            InputLabelProps={{ shrink: true }}
                            value={periodStart}
                            onChange={(e) => handleStartChange(e.target.value)}
                        />
                        <TextField
                            fullWidth size="small" type="datetime-local"
                            label="Period end" InputLabelProps={{ shrink: true }}
                            value={periodEnd}
                            onChange={(e) => { setAutoEnd(false); setPeriodEnd(e.target.value); }}
                            helperText={autoEnd ? `Auto: start + 1 ${cycleLabel}` : 'Manual override'}
                        />
                    </Stack>

                    {/* Live preview so the admin sees the subtotal / discount / total
                        the invoice will carry BEFORE clicking Generate. */}
                    {!isFreemium && (
                        <Box sx={{
                            p: 1.5, borderRadius: '10px',
                            border: '1px solid #e2e8f0',
                            bgcolor: 'rgba(248,250,252,0.6)',
                        }}>
                            <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.3 }}>
                                <Typography sx={{ fontFamily, fontSize: '0.78rem', color: '#64748b' }}>
                                    Subtotal ({cycleLabel}ly)
                                </Typography>
                                <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#1e293b' }}>
                                    {formatCurrency(subtotal, subscription?.plan?.currency || 'usd')}
                                </Typography>
                            </Stack>
                            <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.3 }}>
                                <Typography sx={{ fontFamily, fontSize: '0.78rem', color: '#64748b' }}>
                                    Discount {discountPct > 0
                                        ? `(${discountPct}% off)`
                                        : discountFixed > 0
                                            ? `(${formatCurrency(discountFixed, subscription?.plan?.currency || 'usd')} off)`
                                            : '(none)'}
                                </Typography>
                                <Typography sx={{ fontFamily, fontSize: '0.85rem',
                                                  color: discountAmount > 0 ? '#10b981' : '#94a3b8' }}>
                                    {discountAmount > 0
                                        ? `- ${formatCurrency(discountAmount, subscription?.plan?.currency || 'usd')}`
                                        : '—'}
                                </Typography>
                            </Stack>
                            <Divider sx={{ my: 0.5 }} />
                            <Stack direction="row" justifyContent="space-between">
                                <Typography sx={{ fontFamily, fontSize: '0.85rem', fontWeight: 700, color: '#0f172a' }}>
                                    Total
                                </Typography>
                                <Typography sx={{ fontFamily, fontSize: '0.95rem', fontWeight: 800, color: '#3b82f6' }}>
                                    {formatCurrency(total, subscription?.plan?.currency || 'usd')} / {cycleLabel}
                                </Typography>
                            </Stack>
                        </Box>
                    )}

                    <Stack direction="row" spacing={1.5} alignItems="center">
                        <Switch checked={markPaid} onChange={(e) => setMarkPaid(e.target.checked)} size="small" />
                        <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#475569' }}>
                            Mark as paid
                        </Typography>
                    </Stack>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                        <Switch checked={send} onChange={(e) => setSend(e.target.checked)} size="small" />
                        <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#475569' }}>
                            Email to customer after creating
                        </Typography>
                    </Stack>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={busy}
                        sx={{ fontFamily, textTransform: 'none' }}>
                    Cancel
                </Button>
                <Button onClick={submit} variant="contained" disabled={busy}
                        sx={{ fontFamily, textTransform: 'none', fontWeight: 700 }}>
                    {busy ? <CircularProgress size={18} /> : 'Generate'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default UserDetail;
